import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import { FloorplanPlaceholderPositionKind } from "@domain/project/floorplan/floorplan-placeholder-position-kind";
import { IsString } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export enum FloorplanPlaceholderType {
  ALARM_DEVICE = "alarmDevice",
  PLASTIC_SIGN = "plasticSign",
  SIGNAL_ELEMENT = "signalElement",
  PATIENT_MONITOR = "patientMonitor",
  CENTRAL_STATION = "centralStation",
  TELEMETRY = "telemetry",
  KVM = "kvm",
  DISPLAY = "display",
  UTILITY = "utility",
}

export class FloorplanSimplePlaceholder extends FloorplanPlaceholder {
  @IsString()
  readonly type: string;

  constructor(floorplan: Floorplan, id: string, x: number, y: number, size: number, type: string, notes?: string) {
    super(floorplan, id, x, y, size, notes);
    this.type = type;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanItem) | undefined {
    const size = this.size;
    const type = this.type;

    return (x: number, y: number) => FloorplanSimplePlaceholder.create(this._floorplan, x, y, size, type);
  }

  protected override copySpecificDuplicateStateTo(): ((duplicate: FloorplanItem) => void) | undefined {
    const products = this.capturePlaceholderProducts();

    return (duplicate: FloorplanItem) => {
      this.copyPlaceholderProductsTo(duplicate as FloorplanSimplePlaceholder, products);
    };
  }

  override getDuplicateTargetCollection() {
    return this.getTargetCollection();
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate,
        this.getTargetCollection(),
        this.getAddedEventType(),
        () => this.refreshPlaceholderPositionIds(),
        afterDuplicateAdded,
      );
  }

  setItemTextVisibility() {
    switch (this.type) {
      case FloorplanPlaceholderType.ALARM_DEVICE:
        this.itemTextVisible = this._floorplan.floorplanState.alarmDevicesItemTextVisible;
        break;
      case FloorplanPlaceholderType.PLASTIC_SIGN:
        this.itemTextVisible = this._floorplan.floorplanState.plasticSignsItemTextVisible;
        break;
      case FloorplanPlaceholderType.SIGNAL_ELEMENT:
        this.itemTextVisible = this._floorplan.floorplanState.signalElementsItemTextVisible;
        break;
      case FloorplanPlaceholderType.PATIENT_MONITOR:
        this.itemTextVisible = this._floorplan.floorplanState.patientMonitorsItemTextVisible;
        break;
      case FloorplanPlaceholderType.CENTRAL_STATION:
        this.itemTextVisible = this._floorplan.floorplanState.centralStationsItemTextVisible;
        break;
      case FloorplanPlaceholderType.TELEMETRY:
        this.itemTextVisible = this._floorplan.floorplanState.telemetriesItemTextVisible;
        break;
      case FloorplanPlaceholderType.KVM:
        this.itemTextVisible = this._floorplan.floorplanState.kvmsItemTextVisible;
        break;
      case FloorplanPlaceholderType.DISPLAY:
        this.itemTextVisible = this._floorplan.floorplanState.displaysItemTextVisible;
        break;
      case FloorplanPlaceholderType.UTILITY:
        this.itemTextVisible = this._floorplan.floorplanState.utilitiesItemTextVisible;
        break;
    }
  }

  override delete(): void {
    switch (this.type) {
      case FloorplanPlaceholderType.ALARM_DEVICE:
        this._floorplan.deleteAlarmDevicePlaceholder(this);
        break;
      case FloorplanPlaceholderType.PLASTIC_SIGN:
        this._floorplan.deletePlasticSignPlaceholder(this);
        break;
      case FloorplanPlaceholderType.SIGNAL_ELEMENT:
        this._floorplan.deleteSignalElementPlaceholder(this);
        break;
      case FloorplanPlaceholderType.PATIENT_MONITOR:
        this._floorplan.deletePatientMonitorPlaceholder(this);
        break;
      case FloorplanPlaceholderType.CENTRAL_STATION:
        this._floorplan.deleteCentralStationPlaceholder(this);
        break;
      case FloorplanPlaceholderType.TELEMETRY:
        this._floorplan.deleteTelemetryPlaceholder(this);
        break;
      case FloorplanPlaceholderType.KVM:
        this._floorplan.deleteKvmPlaceholder(this);
        break;
      case FloorplanPlaceholderType.DISPLAY:
        this._floorplan.deleteDisplayPlaceholder(this);
        break;
      case FloorplanPlaceholderType.UTILITY:
        this._floorplan.deleteUtilityPlaceholder(this);
        break;
    }
  }

  private getTargetCollection(): FloorplanItem[] {
    let items: FloorplanSimplePlaceholder[] = [];
    switch (this.type) {
      case FloorplanPlaceholderType.ALARM_DEVICE:
        items = this._floorplan.alarmDevicePlaceholders;
        break;
      case FloorplanPlaceholderType.PLASTIC_SIGN:
        items = this._floorplan.plasticSignPlaceholders;
        break;
      case FloorplanPlaceholderType.SIGNAL_ELEMENT:
        items = this._floorplan.signalElementPlaceholders;
        break;
      case FloorplanPlaceholderType.PATIENT_MONITOR:
        items = this._floorplan.patientMonitorPlaceholders;
        break;
      case FloorplanPlaceholderType.CENTRAL_STATION:
        items = this._floorplan.centralStationPlaceholders;
        break;
      case FloorplanPlaceholderType.TELEMETRY:
        items = this._floorplan.telemetryPlaceholders;
        break;
      case FloorplanPlaceholderType.KVM:
        items = this._floorplan.kvmPlaceholders;
        break;
      case FloorplanPlaceholderType.DISPLAY:
        items = this._floorplan.displayPlaceholders;
        break;
      case FloorplanPlaceholderType.UTILITY:
        items = this._floorplan.utilityPlaceholders;
        break;
    }
    return items;
  }

  protected override refreshFloorplanLockState(): void {
    switch (this.type) {
      case FloorplanPlaceholderType.ALARM_DEVICE:
        this._floorplan.floorplanState.refreshAlarmDevicesLock();
        break;
      case FloorplanPlaceholderType.PLASTIC_SIGN:
        this._floorplan.floorplanState.refreshPlasticSignsLock();
        break;
      case FloorplanPlaceholderType.SIGNAL_ELEMENT:
        this._floorplan.floorplanState.refreshSignalElementsLock();
        break;
      case FloorplanPlaceholderType.PATIENT_MONITOR:
        this._floorplan.floorplanState.refreshPatientMonitorsLock();
        break;
      case FloorplanPlaceholderType.CENTRAL_STATION:
        this._floorplan.floorplanState.refreshCentralStationsLock();
        break;
      case FloorplanPlaceholderType.TELEMETRY:
        this._floorplan.floorplanState.refreshTelemetriesLock();
        break;
      case FloorplanPlaceholderType.KVM:
        this._floorplan.floorplanState.refreshKvmsLock();
        break;
      case FloorplanPlaceholderType.DISPLAY:
        this._floorplan.floorplanState.refreshDisplaysLock();
        break;
      case FloorplanPlaceholderType.UTILITY:
        this._floorplan.floorplanState.refreshUtilitiesLock();
        break;
    }
  }

  static create(floorplan: Floorplan, x: number, y: number, size: number, type: string): FloorplanSimplePlaceholder {
    return new FloorplanSimplePlaceholder(floorplan, uuidv4(), x, y, size, type);
  }

  private getAddedEventType() {
    switch (this.type) {
      case FloorplanPlaceholderType.ALARM_DEVICE:
        return FloorplanEventType.ALARM_DEVICE_PLACEHOLDER_ADDED;
      case FloorplanPlaceholderType.PLASTIC_SIGN:
        return FloorplanEventType.PLASTIC_SIGN_PLACEHOLDER_ADDED;
      case FloorplanPlaceholderType.SIGNAL_ELEMENT:
        return FloorplanEventType.SIGNAL_ELEMENT_PLACEHOLDER_ADDED;
      case FloorplanPlaceholderType.PATIENT_MONITOR:
        return FloorplanEventType.PATIENT_MONITOR_PLACEHOLDER_ADDED;
      case FloorplanPlaceholderType.CENTRAL_STATION:
        return FloorplanEventType.CENTRAL_STATION_PLACEHOLDER_ADDED;
      case FloorplanPlaceholderType.TELEMETRY:
        return FloorplanEventType.TELEMETRY_PLACEHOLDER_ADDED;
      case FloorplanPlaceholderType.KVM:
        return FloorplanEventType.KVM_PLACEHOLDER_ADDED;
      case FloorplanPlaceholderType.DISPLAY:
        return FloorplanEventType.DISPLAY_PLACEHOLDER_ADDED;
      case FloorplanPlaceholderType.UTILITY:
        return FloorplanEventType.UTILITY_PLACEHOLDER_ADDED;
      default:
        throw new Error(`Unsupported placeholder type '${this.type}'`);
    }
  }

  override getPlaceholderPositionKind(): FloorplanPlaceholderPositionKind | undefined {
    switch (this.type) {
      case FloorplanPlaceholderType.ALARM_DEVICE:
        return FloorplanPlaceholderPositionKind.ALARM_DEVICE;
      case FloorplanPlaceholderType.PLASTIC_SIGN:
        return FloorplanPlaceholderPositionKind.PLASTIC_SIGN;
      case FloorplanPlaceholderType.SIGNAL_ELEMENT:
        return FloorplanPlaceholderPositionKind.SIGNAL_ELEMENT;
      case FloorplanPlaceholderType.PATIENT_MONITOR:
        return FloorplanPlaceholderPositionKind.PATIENT_MONITOR;
      case FloorplanPlaceholderType.CENTRAL_STATION:
        return FloorplanPlaceholderPositionKind.CENTRAL_STATION;
      case FloorplanPlaceholderType.TELEMETRY:
        return FloorplanPlaceholderPositionKind.TELEMETRY;
      case FloorplanPlaceholderType.KVM:
        return FloorplanPlaceholderPositionKind.KVM;
      case FloorplanPlaceholderType.DISPLAY:
        return FloorplanPlaceholderPositionKind.DISPLAY;
      case FloorplanPlaceholderType.UTILITY:
        return FloorplanPlaceholderPositionKind.UTILITY;
      default:
        return undefined;
    }
  }
}
